import { i as totalBytesAllowance, n as DEFAULT_ARCHIVE_LIMITS, r as resolveArchiveLimits, t as ArchiveLimitError } from "./archive-limits-CMo2zcn-.js";
import { t as byteCountingStream } from "./byte-counting-stream-hK69xe4i.js";
//#region ../src/storage/capped-zip-entries.ts
/**
* The central directory's entries, walked one at a time and refused once
* there are more than `maxEntries` (a crafted directory can list millions;
* the walk stops one past the cap instead of building an object for each).
* zip.js has read the directory's BYTES whole by then - the size its end
* record declares, in one read - so this cap does not bound them: a reader
* with a single-read cap does (`new ByteSourceReader(source,
* limits.maxDirectoryBytes)`, K0 milestone review R4).
*/
async function listZipEntriesCapped(reader, maxEntries = DEFAULT_ARCHIVE_LIMITS.maxEntries) {
	const entries = [];
	for await (const entry of reader.getEntriesGenerator()) {
		if (entries.length >= maxEntries) throw new ArchiveLimitError("entry-count", maxEntries, entries.length + 1);
		entries.push(entry);
	}
	return entries;
}
function assertCap(name, value) {
	if (!Number.isSafeInteger(value) || value <= 0) throw new RangeError(`DecompressionBudget: ${name} must be a positive integer, got ${String(value)}`);
}
/** One archive's allowance of inflated bytes: per entry and in total. */
var DecompressionBudget = class DecompressionBudget {
	maxEntryBytes;
	maxTotalBytes;
	/** Largest number of bytes any read of each entry produced so far, by
	*  the entry's local header offset. */
	#charged = /* @__PURE__ */ new Map();
	#total = 0;
	constructor(caps) {
		assertCap("maxEntryBytes", caps.maxEntryBytes);
		assertCap("maxTotalBytes", caps.maxTotalBytes);
		this.maxEntryBytes = caps.maxEntryBytes;
		this.maxTotalBytes = caps.maxTotalBytes;
	}
	/** The budget for an archive of `archiveSize` bytes under `limits`
	*  (defaults, with any overrides applied). */
	static forArchive(archiveSize, limits = {}) {
		const resolved = resolveArchiveLimits(limits);
		return new DecompressionBudget({
			maxEntryBytes: resolved.maxEntryBytes,
			maxTotalBytes: totalBytesAllowance(archiveSize, resolved)
		});
	}
	/** Distinct inflated bytes charged so far. */
	get totalBytes() {
		return this.#total;
	}
	/** A read of the entry whose data starts at `offset` has produced
	*  `written` bytes so far; throws once that passes `entryCap` (the
	*  smaller of `maxEntryBytes` and the call's own cap) or the archive
	*  total. One budget serves ONE archive: two archives' offsets collide. */
	charge(offset, written, entryCap) {
		if (written > entryCap) throw new ArchiveLimitError("entry-bytes", entryCap, written);
		const before = this.#charged.get(offset) ?? 0;
		if (written <= before) return;
		const total = this.#total + (written - before);
		if (total > this.maxTotalBytes) throw new ArchiveLimitError("total-bytes", this.maxTotalBytes, total);
		this.#charged.set(offset, written);
		this.#total = total;
	}
};
/**
* Inflate one entry through a byte counter that charges the budget as each
* chunk arrives, handing the counted stream to `consume` as a Response
* body. zip.js writes straight into the counter's writable, so nothing is
* collected in page memory on the way (K0 milestone review R5): a Blob is
* assembled by `Response.blob()` (outside the JS heap in a browser).
*/
async function readCounted(entry, budget, maxEntryBytes, consume) {
	if (maxEntryBytes !== void 0) assertCap("maxEntryBytes", maxEntryBytes);
	const cap = Math.min(budget.maxEntryBytes, maxEntryBytes ?? Infinity);
	/** The cap error, kept in case zip.js wraps the stream's abort reason. */
	let limitError = null;
	const counter = byteCountingStream((written) => {
		try {
			budget.charge(entry.offset, written, cap);
		} catch (err) {
			if (err instanceof ArchiveLimitError) limitError = err;
			throw err;
		}
	});
	const consumed = consume(new Response(counter.readable));
	consumed.catch(() => void 0);
	try {
		await entry.getData(counter.writable, { checkOverlappingEntry: true });
	} catch (err) {
		throw limitError ?? err;
	}
	return consumed;
}
/** One entry inflated to a Blob of `mimeType`, under the budget (and the
*  optional tighter `maxEntryBytes`). */
async function readZipEntryBlob(entry, budget, mimeType, maxEntryBytes) {
	const blob = await readCounted(entry, budget, maxEntryBytes, (body) => body.blob());
	return blob.type === mimeType ? blob : new Blob([blob], { type: mimeType });
}
/** One entry inflated and decoded as UTF-8 text, under the budget (and the
*  optional tighter `maxEntryBytes`, e.g. `maxTextEntryBytes`). Text
*  lives on the JS heap whatever reads it, so the caller's tighter cap is
*  what bounds it. */
async function readZipEntryText(entry, budget, maxEntryBytes) {
	return readCounted(entry, budget, maxEntryBytes, (body) => body.text());
}
//#endregion
export { readZipEntryText as i, listZipEntriesCapped as n, readZipEntryBlob as r, DecompressionBudget as t };
